/* "Häufig zusammen gekauft" — adds every tile's selected variant in one request.

   One /cart/add.js call with an `items` array (not a loop of single adds) so the
   cart drawer re-renders once and the user never sees it flash per product. */
(function () {
  'use strict';
  const warmedImages = new Map();

  function warmImage(src) {
    if (!src || warmedImages.has(src) || warmedImages.size >= 24) return;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    warmedImages.set(src, image);
    image.src = src;
    if (image.decode) image.decode().catch(function () {});
  }

  class ProductBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-bundle-add]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      if (!this.button) return;

      this.button.addEventListener('click', this.addBundle.bind(this));
      this.bindSwatches();
      this.bindColorDropdowns();
      // Prepare the small colour photos before a customer opens the picker.
      this.querySelectorAll('[data-bundle-swatch]').forEach(function (swatch) {
        warmImage(swatch.getAttribute('data-image'));
      });
    }

    // The colour pill dropdown per tile (same UI as the sticky bar). The trigger
    // opens/closes its own popover; outside-clicks close any open one.
    bindColorDropdowns() {
      var self = this;
      this.querySelectorAll('[data-bundle-color-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var dd = btn.closest('[data-bundle-color-dd]');
          if (!dd) return;
          var willOpen = !dd.classList.contains('is-open');
          self.closeColorDropdowns();
          dd.classList.toggle('is-open', willOpen);
          btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        });
      });
      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-bundle-color-dd]')) self.closeColorDropdowns();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeColorDropdowns();
      });
    }

    closeColorDropdowns() {
      this.querySelectorAll('[data-bundle-color-dd].is-open').forEach(function (dd) {
        dd.classList.remove('is-open');
        var t = dd.querySelector('[data-bundle-color-toggle]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    // Swatches only retarget their own tile: the bundle never touches the main
    // variant picker, so choosing a colour here cannot change the page's product.
    bindSwatches() {
      var self = this;
      this.querySelectorAll('[data-bundle-swatch]').forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          var tile = swatch.closest('[data-bundle-item]');
          if (!tile) return;

          tile.querySelectorAll('[data-bundle-swatch]').forEach(function (s) {
            s.classList.toggle('is-selected', s === swatch);
          });
          tile.setAttribute('data-variant-id', swatch.getAttribute('data-variant-id'));
          self.updatePrice(tile, swatch);
          self.updateImage(tile, swatch);

          // Reflect the chosen colour on the closed trigger pill, then close.
          var dd = swatch.closest('[data-bundle-color-dd]');
          if (dd) {
            var trigBar = dd.querySelector('[data-bundle-color-toggle] .sticky--color-current');
            var optBar = swatch.querySelector('.sticky--color-current');
            if (trigBar && optBar) trigBar.style.cssText = optBar.style.cssText;
            dd.classList.remove('is-open');
            var t = dd.querySelector('[data-bundle-color-toggle]');
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
      });
    }

    updateImage(tile, swatch) {
      var src = swatch.getAttribute('data-image');
      var img = tile.querySelector('.product-bundle--media img');
      if (!src || !img) return;

      // The new source is already warmed; do not leave an old responsive source
      // or native lazy-loading delay on a colour selected by the customer.
      img.loading = 'eager';
      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      if (img.hasAttribute('data-src')) img.setAttribute('data-src', src);
      img.setAttribute('data-original', src);
      img.setAttribute('src', src);
      img.classList.remove('lazyloaded', 'lazyloading');
    }

    updatePrice(tile, swatch) {
      var priceBox = tile.querySelector('[data-bundle-price]');
      if (!priceBox) return;

      var price = swatch.getAttribute('data-price');
      var compare = swatch.getAttribute('data-compare-price');
      var actual = priceBox.querySelector('.product-bundle--actual');
      var compareEl = priceBox.querySelector('.product-bundle--compare');

      if (actual && price) actual.innerHTML = price;
      if (actual) actual.classList.toggle('is-sale', !!compare);

      if (compare) {
        if (!compareEl) {
          compareEl = document.createElement('s');
          compareEl.className = 'product-bundle--compare';
          priceBox.insertBefore(compareEl, priceBox.firstChild);
        }
        compareEl.innerHTML = compare;
      } else if (compareEl) {
        compareEl.remove();
      }
    }

    items() {
      var list = [];
      this.querySelectorAll('[data-bundle-item]').forEach(function (tile) {
        var id = tile.getAttribute('data-variant-id');
        if (id) list.push({ id: Number(id), quantity: 1 });
      });
      return list;
    }

    setError(message) {
      if (!this.errorEl) return;
      if (message) {
        this.errorEl.textContent = message;
        this.errorEl.removeAttribute('hidden');
      } else {
        this.errorEl.textContent = '';
        this.errorEl.setAttribute('hidden', '');
      }
    }

    addBundle(event) {
      event?.preventDefault();
      event?.stopPropagation();
      if (this.button.classList.contains('is-loading')) return;
      var self = this;
      var items = this.items();
      if (!items.length) return;

      this.setError('');
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-disabled', 'true');

      if (window.starblizBundleCart?.addItems) {
        window.starblizBundleCart.addItems(items, this.button)
          .catch(function (error) { self.setError(error.message); })
          .finally(function () {
            self.button.classList.remove('is-loading');
            self.button.removeAttribute('aria-disabled');
          });
        return;
      }

      var cart = document.querySelector('cart-drawer');
      var body = { items: items };
      // Ask for the drawer's section markup in the same round-trip; without it
      // renderContents() has nothing to swap in.
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) {
          return s.section;
        });
        body.sections_url = window.location.pathname;
      }

      var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

      fetch(addUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          // /cart/add.js reports failures in the body with a `status` field —
          // an out-of-stock line makes the WHOLE request fail, so nothing is
          // added and there is no partial cart to undo.
          if (data.status) {
            self.setError(data.description || data.message || '');
            return;
          }
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(data);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function (e) {
          console.error(e);
          self.setError('');
        })
        .finally(function () {
          self.button.classList.remove('is-loading');
          self.button.removeAttribute('aria-disabled');
        });
    }
  }

  if (!customElements.get('product-bundle')) {
    customElements.define('product-bundle', ProductBundle);
  }
})();

